import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArticleSearchArea,
  ArticleSearchParamsWithId,
  FacetsStats,
} from '@obeta/models/lib/models/Search'
import { gql, useApolloClient } from '@apollo/client'
import { UserV2 } from '@obeta/models/lib/models/Users/UserV2'
import {
  createAvailabilityRequest,
  createFilterRequest,
  createMainRequest,
  createSuppliersRequest,
  FACET_ETIM_DATA_CLASS_ID,
  FACET_ETIM_DATA_FEATURES,
  FACET_LIST_PRICE,
  FACET_SUPPLIER_NAME,
  FACET_TYPE,
} from '@obeta/utils/lib/search/requests'
import { useRxDB } from 'rxdb-hooks'
import short from 'short-uuid'
import { BehaviorSubject } from 'rxjs'
import { useUserDataV2 } from './useUserDataV2'
import { priceStockSearchQuery } from '../queries/priceStockSearchQuery'
import {
  GetPriceAndStockDataInSearchQueryQuery,
  GetPriceAndStockDataInSearchQueryQueryVariables,
  MultiSearchProductsQuery,
  MultiSearchProductsQueryVariables,
  ProductSearchUserData,
  SearchHubQueryResponse,
} from '@obeta/schema'
import {
  CompleteSearchProduct,
  IncompleteSearchProduct,
  SearchFacet,
  SearchFacetElement,
  SearchProductWithPriceAndStocks,
} from '@obeta/models/lib/schema-models'
import { WAREHOUSE_FACETS } from '@obeta/utils/lib/getFacetByWarehouseId'
import { useWarehouseContext } from '../stores/useWarehouseContext'
import { ArticleSearchFilter } from './useArticleSearchFilter'
import { useSearchFacets } from './useSearchFacets'
import { useHistory } from './useHistoryApi'
import { isInternalLink } from '@obeta/utils/lib/isInternalLink'

export const buildSearchParams = (
  searchParams: ArticleSearchParamsWithId | null | undefined,
  forceImmediatelyAvailable: boolean
): ArticleSearchParamsWithId => {
  return {
    id: '',
    articleSearchArea: forceImmediatelyAvailable
      ? ArticleSearchArea.IMMEDIATELY_AVAILABLE
      : ArticleSearchArea.ALL,
    ...searchParams,
  }
}

export const multiSearchQuery = gql`
  query multiSearchProducts($productMultiSearchRequest: ProductMultiSearchRequest!) {
    multiSearchProducts(input: $productMultiSearchRequest) {
      smartQuery {
        userQuery
        masterQuery
        searchQuery
        redirect
        successful
        relatedQueries {
          query
          relation
        }
        resultModifications {
          ids
          modificationType
        }
      }
      responses {
        nbHits
        nbPages
        facets {
          name
          values {
            value
            count
            meta
          }
          meta
        }
        oxomiToken
        oxomiPortal
        products {
          title
          sapId
          labels {
            ... on Label {
              text
              type
            }
            ... on CustomLabel {
              text
              color
            }
          }
          replacementArticleSapId
          replacementProducts {
            dehaId
          }
          articleDescription
          supplierImageData {
            large
          }
          isDeleted
          isForSale
          isSendable
          isCutProduct
          isDiscontinued
          priceDimension
          minimumAmount
          oxomiId
          obetaId
          dehaId
          unit
          type
          supplierId
          isTopseller
        }
        facetsStats {
          name
          min
          max
        }
        queryId
        userData {
          promotion {
            actionName
            target
            images {
              desktop
              mobile
              tabletSmall
              tabletWide
            }
          }
        }
      }
    }
  }
`
export interface SearchRequest {
  itemsPerPage: number
  page: number
  searchParams?: ArticleSearchParamsWithId
}

type DataUpdatedParams = {
  prevEntities: IncompleteSearchProduct[]
  nextEntities: IncompleteSearchProduct[]
} | null

export type AvailabilityResult = {
  [ArticleSearchArea.ALL]: number
  [ArticleSearchArea.IMMEDIATELY_AVAILABLE]: number
}

export interface SearchResult {
  loading: boolean
  hits: number
  maxPage: number
  searchResult: Array<IncompleteSearchProduct>
  smartQuery: SearchHubQueryResponse | null
  fetchMore: (request: SearchRequest) => Promise<void>
  dataUpdated$: BehaviorSubject<DataUpdatedParams>
  queryId: string
  bannerData: Array<ProductSearchUserData>
  facets: Array<SearchFacet>
  availabilityResult: AvailabilityResult
  suppliersResult: SearchFacetElement[]
  searchFilterResult: ArticleSearchFilter
}

interface SearchMeta {
  currentRequests: Set<string>
  loadedPages: Set<number>
  currentSearchParamsId: string | null
  itemsPerPage: number
  currentWareHouse: string | null
}

const fillArray = <T,>(p: { base: T[]; incoming: T[]; fromIndex: number }) => {
  const { base, incoming, fromIndex } = p

  const cpyBase = [...base] // make sure helper stays pure
  for (let i = 0; i < incoming.length; i += 1) {
    cpyBase[i + fromIndex] = incoming[i]
  }

  return cpyBase
}

const dataUpdated$ = new BehaviorSubject<DataUpdatedParams>(null)

/**
 * using searchParams request and build available etimClasses (classes of features product can have),
 *  etimAttributes (features product can have) and ...
 * @param searchParams
 * @returns
 */
export const useArticleSearch = (
  request: SearchRequest,
  onItemsPerPageChange?: () => void
): SearchResult => {
  const history = useHistory()
  const [searchResult, setSearchResult] = useState<Array<IncompleteSearchProduct>>([])
  const [smartQuery, setSmartQuery] = useState<SearchHubQueryResponse | null>(null)
  const [availabilityResult, setAvailabilityResult] = useState<AvailabilityResult>({
    [ArticleSearchArea.ALL]: 0,
    [ArticleSearchArea.IMMEDIATELY_AVAILABLE]: 0,
  })
  const [suppliersResult, setSuppliersResult] = useState<Array<SearchFacetElement>>([])
  const [searchFilterResult, setSearchFilterResult] = useState<ArticleSearchFilter>({
    suppliers: [],
    hitCount: 0,
    etimClasses: [],
    etimAttributes: [],
    listPriceMin: 0,
    listPriceMax: 0,
  })
  const [facets, setFacets] = useState<Array<SearchFacet>>([])
  const prevSearchResultRev = useRef(searchResult)
  const prevItemsPerPage = useRef(request.itemsPerPage)

  const [hits, setHits] = useState(-1)

  const [maxPage, setMaxPage] = useState(-1)
  const [bannerData, setBannerData] = useState<Array<ProductSearchUserData>>([])
  const [loading, setLoading] = useState(false)
  const { isLoggedIn } = useUserDataV2()
  const [queryId, setQueryId] = useState('')
  const { warehouseId } = useWarehouseContext()

  const metaRef = useRef<SearchMeta>({
    currentRequests: new Set(),
    loadedPages: new Set(),
    currentSearchParamsId: null,
    itemsPerPage: request.itemsPerPage,
    currentWareHouse: warehouseId,
  })

  const client = useApolloClient()
  const db = useRxDB()

  useEffect(() => {
    dataUpdated$.next({ prevEntities: prevSearchResultRev.current, nextEntities: searchResult })
    prevSearchResultRev.current = searchResult
  }, [searchResult])

  const searchFacets = useSearchFacets(buildSearchParams(request.searchParams, true))

  const fetchMore = useCallback(
    async (request: SearchRequest) => {
      const params = request.searchParams
      if (!params) {
        return
      }

      const reset =
        metaRef.current.currentSearchParamsId !== params.id ||
        metaRef.current.itemsPerPage !== request.itemsPerPage ||
        metaRef.current.currentWareHouse !== warehouseId

      if (reset) {
        metaRef.current.currentSearchParamsId = params.id
        metaRef.current.itemsPerPage = request.itemsPerPage
        metaRef.current.currentRequests = new Set() // invalidate all current requests
        metaRef.current.loadedPages = new Set()
        metaRef.current.currentWareHouse = warehouseId
      } else if (metaRef.current.loadedPages.has(request.page)) {
        return // terminate if page was cached already.
      }

      const reqId = short.generate()
      metaRef.current.currentRequests.add(reqId)

      setLoading(true)

      const didItemsPerPageChange = prevItemsPerPage.current !== request.itemsPerPage

      const page = didItemsPerPageChange ? 0 : request.page

      const searchFilterRequestFacets = [
        FACET_TYPE,
        ...WAREHOUSE_FACETS,
        FACET_ETIM_DATA_CLASS_ID,
        FACET_LIST_PRICE,
      ]
      if (params.selectedEtim?.id && params.selectedEtim.id !== '0') {
        searchFilterRequestFacets.push(`${FACET_ETIM_DATA_FEATURES}.${params.selectedEtim?.id}.*`)
      }
      params.etim?.forEach((el) => {
        if (el.facet) {
          searchFilterRequestFacets.push(el.facet.name)
        }
      })
      const res = await client.query<MultiSearchProductsQuery, MultiSearchProductsQueryVariables>({
        query: multiSearchQuery,
        variables: {
          productMultiSearchRequest: {
            exactSearch: request.searchParams?.exactSearch,
            requests: [
              createMainRequest(params, request.itemsPerPage, page, warehouseId, searchFacets),
              createAvailabilityRequest(params, warehouseId),
              createSuppliersRequest(params, warehouseId),
              createFilterRequest(params, warehouseId, searchFilterRequestFacets),
            ],
          },
        },
        fetchPolicy: 'no-cache',
      })

      if (didItemsPerPageChange && onItemsPerPageChange) {
        onItemsPerPageChange()
      }

      prevItemsPerPage.current = request.itemsPerPage
      const [mainSearchResponse, availabilityResponse, suppliersResponse, searchFilterResponse] =
        res.data.multiSearchProducts.responses
      const indexBase = request.page * request.itemsPerPage

      if (!metaRef.current.currentRequests.has(reqId)) {
        // current request was invalidated.
        return
      }

      if (reset) {
        setSearchResult(() => {
          return fillArray({
            base: [],
            incoming: mainSearchResponse.products,
            fromIndex: indexBase,
          })
        })
      } else {
        setSearchResult((curr) => {
          return fillArray<IncompleteSearchProduct>({
            base: curr,
            incoming: mainSearchResponse.products,
            fromIndex: indexBase,
          })
        })
      }

      delete metaRef.current.currentRequests[reqId]
      metaRef.current.loadedPages.add(request.page)

      // selected filter hints
      setHits(mainSearchResponse.nbHits)

      setMaxPage(mainSearchResponse.nbPages)
      setQueryId(mainSearchResponse.queryId)
      setBannerData(mainSearchResponse.userData)
      setFacets(mainSearchResponse.facets ?? [])

      const smartQueryResult = res.data.multiSearchProducts.smartQuery
      const { redirect } = smartQueryResult

      if (redirect) {
        if (isInternalLink(redirect)) {
          history.replace(redirect)
        } else {
          window.open(redirect, '_blank', 'noopener,noreferrer')
        }
      }

      setSmartQuery(smartQueryResult)

      // availability result
      const allHints = availabilityResponse.facets?.[0]?.values.reduce(
        (prevVal, val) => prevVal + val.count,
        0
      )
      const immediatelyAvailHints =
        availabilityResponse.facets?.[0]?.values.find((el) => el.value === 'true')?.count || 0
      setAvailabilityResult({
        [ArticleSearchArea.ALL]: allHints,
        [ArticleSearchArea.IMMEDIATELY_AVAILABLE]: immediatelyAvailHints,
      })

      // suppliers result
      const suppliersFacets: SearchFacetElement[] =
        suppliersResponse.facets.find((facet) => facet.name === FACET_SUPPLIER_NAME)?.values || []
      setSuppliersResult(suppliersFacets)

      // search filter result
      if (searchFilterResponse) {
        const searchFilterResponseFacets: SearchFacet[] = searchFilterResponse.facets
        const facetsStats: FacetsStats[] = searchFilterResponse.facetsStats
        let etimAttributes: SearchFacet[] = []
        const searchParams = params
        if (searchParams && params?.selectedEtim?.id !== '0') {
          etimAttributes = searchFilterResponseFacets.filter((facet) =>
            facet.name.startsWith(`${FACET_ETIM_DATA_FEATURES}.${searchParams.selectedEtim?.id}`)
          )
        }
        setSearchFilterResult({
          etimClasses:
            searchFilterResponseFacets.find((facet) => facet.name === FACET_ETIM_DATA_CLASS_ID)
              ?.values || [],
          etimAttributes: etimAttributes,
          suppliers:
            searchFilterResponseFacets.find((facet) => facet.name === FACET_SUPPLIER_NAME)
              ?.values || [], // remove
          hitCount: searchFilterResponse.nbHits,
          listPriceMin:
            facetsStats.find((facetsStats) => facetsStats.name === FACET_LIST_PRICE)?.min || 0,
          listPriceMax:
            facetsStats.find((facetsStats) => facetsStats.name === FACET_LIST_PRICE)?.max || 100000, //probably never used cause min/max always come from initial search result
        })
      }

      if (mainSearchResponse) {
        setLoading(false)
      }

      // fire and forget, do not await result
      db.upsertLocal('oxomi', {
        token: mainSearchResponse.oxomiToken,
        portal: mainSearchResponse.oxomiPortal,
      })

      const userv2 = await db.getLocal<UserV2>('userv2')

      // decouple prices and stock from processing of articles
      const userStoreId = userv2?.get('settings')?.defaultStoreId
      if (!userStoreId) {
        // Sentry.captureException(new Error('user store id is undefined')) // TODO replace former Sentry-Code with DataDog
      }
      const sapIds = mainSearchResponse.products.map((prod) => prod.sapId)
      if (sapIds.length > 0 && isLoggedIn) {
        try {
          const results = await client.query<
            GetPriceAndStockDataInSearchQueryQuery,
            GetPriceAndStockDataInSearchQueryQueryVariables
          >({
            query: priceStockSearchQuery,
            variables: {
              sapIds,
              warehouseIds: [userStoreId, warehouseId].filter(Boolean),
            },
          })

          const productData: SearchProductWithPriceAndStocks[] = results.data.getProducts
          setSearchResult((current) => {
            productData.forEach((data) => {
              const el = current.find((p) => p?.sapId === data?.sapId)
              if (el) {
                // Add stock and prices to transform IncompleteProduct to a CompleteProduct
                ;(el as CompleteSearchProduct).stock = data.stock
                ;(el as CompleteSearchProduct).prices = data.prices
                ;(el as CompleteSearchProduct).stockAvailabilityEstimate =
                  data.stockAvailabilityEstimate
              }
            })
            return [...current]
          })
        } catch (err) {
          // Sentry.captureException(err) // TODO replace former Sentry-Code with DataDog
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, db, isLoggedIn, warehouseId, searchFacets]
  )

  /**
   * TODO: queue: new request waits until previous one is finished ?
   */
  useEffect(() => {
    fetchMore(request)
  }, [fetchMore, request])

  return {
    searchResult,
    smartQuery,
    loading,
    hits,
    maxPage,
    fetchMore,
    dataUpdated$,
    queryId,
    bannerData,
    facets,
    availabilityResult,
    suppliersResult,
    searchFilterResult,
  }
}
